import { PINCH_ENTER, PINCH_EXIT } from "../config/constants.ts";
import { landmarkDist, landmarkDist2 } from "../utils/math.ts";
import type { Landmark } from "../utils/types.ts";
import {
  FINGERS,
  INDEX_MCP,
  INDEX_TIP,
  MIDDLE_MCP,
  PINKY_MCP,
  THUMB_IP,
  THUMB_TIP,
  WRIST,
  assertHand,
} from "./landmarks.ts";

export function palmSize(landmarks: Landmark[]): number {
  const pts = assertHand(landmarks);
  const wrist = pts[WRIST]!;
  const middle = pts[MIDDLE_MCP]!;
  const index = pts[INDEX_MCP]!;
  const pinky = pts[PINKY_MCP]!;
  const a = landmarkDist2(wrist, middle);
  const b = landmarkDist2(index, pinky);
  return Math.max(0.04, (a + b) * 0.5);
}

export function isFingerExtended(
  landmarks: Landmark[],
  mcp: number,
  pip: number,
  tip: number,
): boolean {
  const pts = assertHand(landmarks);
  const wrist = pts[WRIST]!;
  const tipPt = pts[tip]!;
  const pipPt = pts[pip]!;
  const mcpPt = pts[mcp]!;
  const dTipWrist = landmarkDist2(tipPt, wrist);
  const dMcpWrist = landmarkDist2(mcpPt, wrist);
  const dTipMcp = landmarkDist2(tipPt, mcpPt);
  const dPipMcp = landmarkDist2(pipPt, mcpPt);
  return dTipWrist > dMcpWrist * 1.35 && dTipMcp > dPipMcp * 1.2;
}

export function extendedFingerCount(landmarks: Landmark[]): number {
  let count = 0;
  for (const finger of FINGERS) {
    if (isFingerExtended(landmarks, finger.mcp, finger.pip, finger.tip)) count += 1;
  }
  return count;
}

export function isThumbExtended(landmarks: Landmark[]): boolean {
  const pts = assertHand(landmarks);
  return (
    landmarkDist2(pts[THUMB_TIP]!, pts[PINKY_MCP]!) >
    landmarkDist2(pts[THUMB_IP]!, pts[PINKY_MCP]!) * 1.08
  );
}

export function isOpenPalm(landmarks: Landmark[]): boolean {
  return extendedFingerCount(landmarks) >= 3 && isThumbExtended(landmarks);
}

export function isClosedFist(landmarks: Landmark[]): boolean {
  const pts = assertHand(landmarks);
  const size = palmSize(pts);
  if (extendedFingerCount(pts) > 1) return false;
  const palm = pts[MIDDLE_MCP]!;
  const tips = [INDEX_TIP, 12, 16, 20].map((i) => pts[i]!);
  const avgToPalm = tips.reduce((sum, tip) => sum + landmarkDist2(tip, palm), 0) / tips.length;
  return avgToPalm < size * 0.9;
}

/**
 * Pinch distance is divided by palm size so the threshold stays usable when
 * the user moves closer to or farther from the camera.
 */
export function pinchDistanceNormalized(landmarks: Landmark[]): number {
  const pts = assertHand(landmarks);
  return landmarkDist(pts[THUMB_TIP]!, pts[INDEX_TIP]!) / palmSize(pts);
}

export function isPinch(distance: number, currentlyPinching: boolean): boolean {
  return currentlyPinching ? distance < PINCH_EXIT : distance < PINCH_ENTER;
}

export function twoHandDistance(a: Landmark[], b: Landmark[]): number {
  return landmarkDist2(assertHand(a)[WRIST]!, assertHand(b)[WRIST]!);
}

export function twoHandMidpoint(a: Landmark[], b: Landmark[]): Landmark {
  const left = assertHand(a)[WRIST]!;
  const right = assertHand(b)[WRIST]!;
  return {
    x: (left.x + right.x) / 2,
    y: (left.y + right.y) / 2,
    z: (left.z + right.z) / 2,
  };
}

export function mirrorLandmarks(landmarks: Landmark[]): Landmark[] {
  return landmarks.map((point) => ({ x: 1 - point.x, y: point.y, z: point.z }));
}
